import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './DashboardSidebar.css';
import { DEFAULT_USER_AVATAR } from '../../constants/media';
import {
  FiArrowLeft,
  FiArrowRight,
  FiUser,
  FiShield,
  FiStar,
  FiInfo,
  FiGrid,
  FiLayers,
  FiHelpCircle,
  FiEdit2,
  FiX,
} from 'react-icons/fi';

const SIDEBAR_COLLAPSE_STORAGE_KEY = 'dashboardSidebarCollapsed';
const QUICK_ACTIONS_STORAGE_KEY = 'dashboardSidebarQuickActions';
const DEFAULT_QUICK_ACTION_KEYS = ['marketplace', 'feed', 'help'];
const MAX_QUICK_ACTIONS = 5;

const STATIC_QUICK_ACTIONS = [
  {
    key: 'marketplace',
    label: 'Ir a marketplace',
    href: '/marketplace',
    icon: FiGrid,
    group: 'Sitio Flotte',
  },
  {
    key: 'feed',
    label: 'Ir al feed',
    href: '/dashboard',
    icon: FiLayers,
    group: 'Sitio Flotte',
  },
  {
    key: 'help',
    label: 'Centro de ayuda',
    href: '/ayuda',
    icon: FiHelpCircle,
    group: 'Sitio Flotte',
  },
];

const areArraysEqual = (a, b) => a.length === b.length && a.every((value, index) => value === b[index]);

export default function DashboardSidebar({ accountCard, navSections = [], onNavigate }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY) === 'true';
  });
  const [isEditingQuickActions, setIsEditingQuickActions] = useState(false);
  const [quickActionKeys, setQuickActionKeys] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_QUICK_ACTION_KEYS;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(QUICK_ACTIONS_STORAGE_KEY) || '[]');
      return Array.isArray(stored) && stored.length ? stored : DEFAULT_QUICK_ACTION_KEYS;
    } catch {
      return DEFAULT_QUICK_ACTION_KEYS;
    }
  });
  const card = accountCard || {};
  const name = card.name || 'Mi perfil';
  const headline = card.headline || card.email || '';
  const subscriptionStatus = String(card.subscriptionStatus || '').trim();
  const avatar = card.avatar || DEFAULT_USER_AVATAR;
  const showVerificationReminder = Boolean(card.verificationCTA);
  const verificationHref = card.verificationHref || '/dashboard?section=empresa-verificacion';
  const normalizedStatus = subscriptionStatus.toLowerCase();
  const showSubscriptionCTA =
    !normalizedStatus || normalizedStatus === 'plan sin asignar' || normalizedStatus === 'free';
  const subscriptionHref = showSubscriptionCTA
    ? '/dashboard?section=suscripciones'
    : '/dashboard/suscripciones/beneficios';
  const collapseLabel = isCollapsed ? 'Expandir barra lateral' : 'Contraer barra lateral';
  const profileHref = card.profileHref || '';
  const ProfileCardComponent = profileHref ? Link : 'div';
  const profileCardProps = profileHref
    ? {
        to: profileHref,
        className: 'sidebar-profile-card sidebar-profile-card--link',
        title: `Ver perfil público de ${name}`,
        'aria-label': `Ver perfil público de ${name}`,
      }
    : {
        className: 'sidebar-profile-card',
      };

  const handleToggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, String(isCollapsed));
    if (isCollapsed) {
      setIsEditingQuickActions(false);
    }
  }, [isCollapsed]);

  const dynamicQuickActionOptions = useMemo(() => {
    if (!Array.isArray(navSections)) return [];
    const list = [];
    navSections.forEach(section => {
      section?.items?.forEach(item => {
        if (!item?.key) return;
        list.push({
          key: item.key,
          label: item.label,
          icon: item.icon || FiGrid,
          toSection: item.key,
          group: section?.label || 'Panel',
        });
      });
    });
    return list;
  }, [navSections]);

  const quickActionMap = useMemo(() => {
    const map = new Map();
    STATIC_QUICK_ACTIONS.forEach(action => {
      map.set(action.key, action);
    });
    dynamicQuickActionOptions.forEach(action => {
      map.set(action.key, action);
    });
    return map;
  }, [dynamicQuickActionOptions]);

  const fallbackQuickActionKeys = useMemo(() => {
    const fallback = [];
    DEFAULT_QUICK_ACTION_KEYS.forEach(key => {
      if (quickActionMap.has(key) && fallback.length < MAX_QUICK_ACTIONS) {
        fallback.push(key);
      }
    });
    if (!fallback.length) {
      for (const key of quickActionMap.keys()) {
        if (fallback.length >= MAX_QUICK_ACTIONS) break;
        fallback.push(key);
      }
    }
    return fallback.length ? fallback : DEFAULT_QUICK_ACTION_KEYS.slice(0, MAX_QUICK_ACTIONS);
  }, [quickActionMap]);

  useEffect(() => {
    if (!quickActionMap.size) return;
    setQuickActionKeys(prev => {
      const filtered = (prev.length ? prev : fallbackQuickActionKeys)
        .filter(key => quickActionMap.has(key))
        .slice(0, MAX_QUICK_ACTIONS);
      if (!filtered.length) {
        return fallbackQuickActionKeys;
      }
      if (areArraysEqual(filtered, prev)) {
        return prev;
      }
      return filtered;
    });
  }, [quickActionMap, fallbackQuickActionKeys]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(QUICK_ACTIONS_STORAGE_KEY, JSON.stringify(quickActionKeys));
  }, [quickActionKeys]);

  const quickActionOptionsList = useMemo(() => {
    return Array.from(quickActionMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'es')
    );
  }, [quickActionMap]);

  const quickActions = useMemo(() => {
    const selected = quickActionKeys
      .map(key => quickActionMap.get(key))
      .filter(Boolean);
    if (selected.length) return selected;
    return fallbackQuickActionKeys.map(key => quickActionMap.get(key)).filter(Boolean);
  }, [quickActionKeys, quickActionMap, fallbackQuickActionKeys]);

  const handleQuickActionToggle = key => {
    if (!quickActionMap.has(key)) return;
    setQuickActionKeys(prev => {
      const exists = prev.includes(key);
      if (exists) {
        const next = prev.filter(current => current !== key);
        return next.length ? next : fallbackQuickActionKeys;
      }
      if (prev.length >= MAX_QUICK_ACTIONS) {
        return prev;
      }
      return [...prev, key];
    });
  };

  const handleQuickActionNavigate = action => {
    if (action?.toSection && typeof onNavigate === 'function') {
      onNavigate(action.toSection);
      return;
    }
    if (action?.href) {
      window.location.assign(action.href);
    }
  };

  const collapsedQuickActions = [
    profileHref
      ? {
          key: 'profile',
          label: 'Ver perfil público',
          to: profileHref,
          icon: FiUser,
        }
      : null,
    showVerificationReminder
      ? {
          key: 'verification',
          label: 'Ir a verificación',
          href: verificationHref,
          icon: FiShield,
        }
      : null,
    {
      key: 'subscription',
      label: showSubscriptionCTA ? 'Hazte premium' : 'Ver plan actual',
      href: subscriptionHref,
      icon: FiStar,
    },
    {
      key: 'about',
      label: 'Sobre Flotte',
      href: '/sobre-nosotros',
      icon: FiInfo,
    },
  ].filter(Boolean);

  const reachedQuickActionLimit = quickActionKeys.length >= MAX_QUICK_ACTIONS;

  return (
    <aside className={`dashboard-sidebar${isCollapsed ? ' dashboard-sidebar--collapsed' : ''}`}>
      <section className="dashboard-sidebar__quick-card" aria-label="Accesos rápidos del panel">
        <header className="dashboard-sidebar__quick-card-head">
          <span className="dashboard-sidebar__quick-card-title">Accesos rápidos</span>
          <button
            type="button"
            className="dashboard-sidebar__quick-edit"
            onClick={() => setIsEditingQuickActions(prev => !prev)}
            aria-pressed={isEditingQuickActions}
            aria-label="Personalizar accesos rápidos"
            title="Personalizar accesos rápidos"
          >
            <FiEdit2 aria-hidden="true" focusable="false" />
            <span>{isEditingQuickActions ? 'Cerrar' : 'Personalizar'}</span>
          </button>
        </header>
        <div className="dashboard-sidebar__top-actions">
          <nav className="dashboard-sidebar__quick-nav" aria-label="Enlaces directos">
            {quickActions.length ? (
              quickActions.map(action => {
                const IconComponent = action.icon || FiGrid;
                const isButton = Boolean(action.toSection && typeof onNavigate === 'function');
                if (isButton) {
                  return (
                    <button
                      type="button"
                      key={action.key}
                      className="dashboard-sidebar__quick-nav-button"
                      onClick={() => handleQuickActionNavigate(action)}
                      aria-label={action.label}
                      title={action.label}
                      data-tooltip={action.label}
                    >
                      <IconComponent aria-hidden="true" focusable="false" />
                    </button>
                  );
                }
                return (
                  <a
                    key={action.key}
                    href={action.href}
                    className="dashboard-sidebar__quick-nav-button"
                    title={action.label}
                    aria-label={action.label}
                    data-tooltip={action.label}
                  >
                    <IconComponent aria-hidden="true" focusable="false" />
                  </a>
                );
              })
            ) : (
              <span className="dashboard-sidebar__quick-empty">
                Selecciona accesos rápidos para mostrarlos aquí.
              </span>
            )}
          </nav>
          <button
            type="button"
            className={`sidebar-collapse-toggle${isCollapsed ? ' sidebar-collapse-toggle--collapsed' : ''}`}
            onClick={handleToggleCollapse}
            aria-expanded={!isCollapsed}
            aria-label={collapseLabel}
            title={collapseLabel}
            data-tooltip={collapseLabel}
          >
            <span className="sidebar-collapse-toggle__chevron" aria-hidden="true">
              {isCollapsed ? <FiArrowRight focusable="false" /> : <FiArrowLeft focusable="false" />}
            </span>
          </button>
        </div>
        {isEditingQuickActions ? (
          <div className="dashboard-sidebar__quick-editor">
            <div className="dashboard-sidebar__quick-editor-header">
              <strong>Elige hasta {MAX_QUICK_ACTIONS}</strong>
              <button
                type="button"
                className="dashboard-sidebar__quick-editor-close"
                onClick={() => setIsEditingQuickActions(false)}
                aria-label="Cerrar personalización"
              >
                <FiX aria-hidden="true" focusable="false" />
              </button>
            </div>
            <p className="dashboard-sidebar__quick-editor-hint">
              Activa los accesos directos que usas con más frecuencia.
            </p>
            <div className="dashboard-sidebar__quick-editor-options">
              {quickActionOptionsList.map(option => {
                const IconComponent = option.icon || FiGrid;
                const isSelected = quickActionKeys.includes(option.key);
                const disabled = !isSelected && reachedQuickActionLimit;
                return (
                  <label
                    key={option.key}
                    className={`quick-editor-option${isSelected ? ' is-selected' : ''}${
                      disabled ? ' is-disabled' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleQuickActionToggle(option.key)}
                      disabled={disabled}
                    />
                    <span className="quick-editor-option__icon" aria-hidden="true">
                      <IconComponent focusable="false" />
                    </span>
                    <span className="quick-editor-option__labels">
                      <span>{option.label}</span>
                      {option.group ? <small>{option.group}</small> : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
      {collapsedQuickActions.length ? (
        <div
          className={`dashboard-sidebar__collapsed-card${
            isCollapsed ? ' dashboard-sidebar__collapsed-card--visible' : ''
          }`}
          aria-hidden={!isCollapsed}
        >
          <nav
            className={`dashboard-sidebar__collapsed-icons${
              isCollapsed ? ' dashboard-sidebar__collapsed-icons--visible' : ''
            }`}
            aria-label="Accesos rápidos del panel"
          >
            {collapsedQuickActions.map(action => {
              const IconComponent = action.icon;
              const ActionComponent = action.to ? Link : 'a';
              const actionProps = action.to ? { to: action.to } : { href: action.href };
              return (
                <ActionComponent
                  key={action.key}
                  {...actionProps}
                  className="dashboard-sidebar__icon-button"
                  title={action.label}
                  aria-label={action.label}
                  data-tooltip={action.label}
                >
                  <IconComponent aria-hidden="true" focusable="false" />
                </ActionComponent>
              );
            })}
          </nav>
        </div>
      ) : null}
      <div className="dashboard-sidebar__content" aria-hidden={isCollapsed}>
        <ProfileCardComponent {...profileCardProps}>
          <div className="sidebar-profile-card__compact">
            <div className="sidebar-profile-card__avatar sidebar-profile-card__avatar--compact">
              <img
                src={avatar}
                alt={`Foto de ${name}`}
                data-placeholder={card.avatar ? undefined : 'true'}
              />
            </div>
            <div className="sidebar-profile-card__info">
              <strong className="sidebar-profile-card__name sidebar-profile-card__name--compact">
                {name}
              </strong>
              {headline ? (
                <span className="sidebar-profile-card__email" title={headline}>
                  {headline}
                </span>
              ) : null}
            </div>
          </div>
        </ProfileCardComponent>

        <nav className="sidebar-footer-links" aria-label="Enlaces legales y compañia">
          <a href="/sobre-nosotros">Sobre nosotros</a>
          <a href="/privacidad">Privacidad</a>
          <a href="/terminos">Términos y condiciones</a>
          <a href="/publicidad">Publicidad</a>
        </nav>
      </div>
    </aside>
  );
}
